import { useState } from "react";
import { Megaphone, MapPin, HelpCircle, Rocket, PhoneCall, Navigation, Sparkles } from "lucide-react";
import { useLang } from "@/lib/i18n";

const ACTIONS = [
  { id: "notice", icon: Megaphone, labelKey: "quickNotice" },
  { id: "address", icon: MapPin, labelKey: "quickAddress" },
  { id: "help", icon: HelpCircle, labelKey: "quickHelp" },
  { id: "coming", icon: Rocket, labelKey: "comingSoon" },
];

export default function QuickActions({ biz }) {
  const { t } = useLang();
  const [open, setOpen] = useState(null);
  const b = biz || {};
  const numbers = (b.contact_numbers || []).slice(0, 2);
  const fullAddress = b.stall_no ? `${b.stall_no}, ${b.landmark}, ${b.district}, ${b.state} – ${b.pin}, ${b.country}` : "";

  const callLinks = (testid) => (
    <div className="mt-3 space-y-1.5">
      {numbers.map((n, i) => (
        <a key={n} href={`tel:+91${n}`} data-testid={`${testid}-call-${i}`} className="flex items-center gap-2 text-sm font-bold text-blue-700 hover:text-blue-900 transition-colors">
          <PhoneCall className="h-4 w-4" /> +91 {n}
        </a>
      ))}
    </div>
  );

  return (
    <div className="fixed bottom-4 right-3 sm:right-5 z-50 flex items-end gap-2" data-testid="quick-actions">
      {open && (
        <div className="sf-card w-72 max-w-[calc(100vw-5.5rem)] p-4 shadow-xl" data-testid={`quick-panel-${open}`}>
          {open === "notice" && (
            <div>
              <div className="flex items-center gap-2 font-display font-bold text-slate-900 text-sm">
                <Megaphone className="h-4 w-4 text-emerald-700" /> {t("quickNotice")}
              </div>
              {b.announcement_active && b.announcement ? (
                <p className="text-sm text-slate-700 mt-2.5">{b.announcement}</p>
              ) : (
                <p className="text-sm text-slate-600 mt-2.5">{t("noticeNone")}</p>
              )}
              {callLinks("quick-notice")}
            </div>
          )}
          {open === "address" && (
            <div>
              <div className="flex items-center gap-2 font-display font-bold text-slate-900 text-sm">
                <MapPin className="h-4 w-4 text-emerald-700" /> {t("quickAddress")}
              </div>
              <p className="text-sm font-semibold text-slate-700 mt-2.5 leading-relaxed">{fullAddress}</p>
              <a href={b.maps_link || "#"} target="_blank" rel="noopener noreferrer" data-testid="quick-directions-link" className="sf-btn-primary w-full !py-2.5 !text-sm mt-3">
                <Navigation className="h-4 w-4" /> {t("getDirections")}
              </a>
            </div>
          )}
          {open === "help" && (
            <div>
              <div className="flex items-center gap-2 font-display font-bold text-slate-900 text-sm">
                <HelpCircle className="h-4 w-4 text-emerald-700" /> {t("quickHelp")}
              </div>
              <ul className="text-sm text-slate-700 space-y-2 mt-2.5">
                <li className="flex gap-2"><span className="text-emerald-700 font-bold">•</span> {t("helpLogin")}</li>
                <li className="flex gap-2"><span className="text-emerald-700 font-bold">•</span> {t("helpDealer")}</li>
              </ul>
              {callLinks("quick-help")}
            </div>
          )}
          {open === "coming" && (
            <div>
              <div className="flex items-center gap-2 font-display font-bold text-slate-900 text-sm">
                <Rocket className="h-4 w-4 text-emerald-700" /> {t("comingSoon")}
              </div>
              <ul className="text-sm text-slate-700 space-y-2 mt-2.5">
                {[t("comingPesticide"), t("comingPayment"), t("comingSms")].map((x, i) => (
                  <li key={i} className="flex gap-2"><Sparkles className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" /> {x}</li>
                ))}
              </ul>
              <p className="text-xs text-slate-400 mt-2.5">{t("moreSoon")}</p>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-col gap-2">
       {b.whatsapp_link && <a href={b.whatsapp_link} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="h-10 w-10 rounded-full flex items-center justify-center bg-[#25D366] shadow-lg hover:scale-110 transition-transform"><svg viewBox="0 0 24 24" className="h-6 w-6 text-white" fill="currentColor"><path d="M20.52 3.48A11.8 11.8 0 0 0 12.04 0C5.5 0 .17 5.33.17 11.87c0 2.09.55 4.13 1.6 5.93L.06 24l6.35-1.67a11.84 11.84 0 0 0 5.63 1.43h.01c6.54 0 11.87-5.33 11.87-11.87 0-3.17-1.23-6.15-3.4-8.41ZM12.05 21.7a9.82 9.82 0 0 1-5.01-1.37l-.36-.21-3.77.99 1-3.67-.23-.38a9.83 9.83 0 0 1-1.5-5.19c0-5.42 4.41-9.83 9.84-9.83 2.63 0 5.1 1.03 6.96 2.9a9.78 9.78 0 0 1 2.87 6.97c0 5.42-4.41 9.83-9.83 9.83Zm5.39-7.37c-.29-.15-1.71-.84-1.98-.94-.27-.1-.47-.15-.67.15-.2.29-.76.94-.93 1.13-.17.2-.34.22-.63.07-.29-.15-1.23-.45-2.34-1.44-.87-.77-1.45-1.72-1.62-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.91-2.2-.24-.58-.48-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.29-1.03 1.01-1.03 2.47s1.06 2.86 1.21 3.05c.15.2 2.08 3.18 5.04 4.46.7.3 1.25.48 1.68.62.71.23 1.36.2 1.87.12.57-.08 1.71-.7 1.95-1.38.24-.68.24-1.27.17-1.39-.07-.12-.27-.2-.56-.34Z"/></svg></a>}
       {b.facebook_link && <a href={b.facebook_link} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="h-10 w-10 rounded-full flex items-center justify-center bg-[#1877F2] shadow-lg hover:scale-110 transition-transform"><svg viewBox="0 0 24 24" className="h-6 w-6 text-white" fill="currentColor"><path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11 10.13 11.93v-8.43H7.08v-3.5h3.05V9.41c0-3.03 1.79-4.7 4.56-4.7 1.32 0 2.7.24 2.7.24v2.99h-1.52c-1.5 0-1.97.94-1.97 1.9v2.28h3.35l-.54 3.5H13.9V24C19.61 23.07 24 18.09 24 12.07Z"/></svg></a>}
        {ACTIONS.map((a) => (
          <div key={a.id} className="relative group">
            <button
              data-testid={`quick-${a.id}-btn`}
              aria-label={t(a.labelKey)}
              onClick={() => setOpen(open === a.id ? null : a.id)}
              className={`h-10 w-10 rounded-full flex items-center justify-center shadow-lg transition focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-emerald-500 ${
                open === a.id ? "bg-emerald-700 text-white" : "bg-[#0B192C] text-white hover:bg-[#1E3E62]"
              }`}
            >
              <a.icon className="h-[18px] w-[18px]" />
            </button>
            <span className="hidden md:block absolute right-12 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[#0B192C] text-white text-xs font-bold px-2.5 py-1.5 shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
              {t(a.labelKey)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
